import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/UI/Button';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { AgentPlatformTableActionsCell } from '../../../components/AgentPlatform/AgentPlatformTableActionsCell';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { ConfirmModal } from '../../../components/UI/ConfirmModal';
import { useToast } from '../../../components/UI/ToastProvider';
import { AGENT_RUNTIME_IDS } from '../../../constants/agentPlatform';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type { AgentDto } from '../../../types/agentPlatform';
import { labelAgentStatus, labelAgentType } from '../../../utils/agentPlatformLabels';
import { apTheme } from '../../../utils/agentPlatformTheme';

export const AgentsListPage: React.FC = () => {
  const [agents, setAgents] = useState<AgentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', description: '' });
  const [deleteTarget, setDeleteTarget] = useState<AgentDto | null>(null);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const list = await agentPlatformService.getAgents();
      setAgents(list);
    } catch {
      addToast('Não foi possível carregar assistentes.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await agentPlatformService.createAgent({
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        agentRuntimeId: AGENT_RUNTIME_IDS.langGraph,
      });
      addToast('Assistente criado.', 'success');
      setShowCreate(false);
      setForm({ code: '', name: '', description: '' });
      navigate(`/agent-platform/agents/${created.id}`);
    } catch {
      addToast('Erro ao criar assistente. Verifique o código único.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await agentPlatformService.deleteAgent(deleteTarget.id);
      addToast('Assistente removido.', 'success');
      setDeleteTarget(null);
      load();
    } catch {
      addToast('Não foi possível remover o assistente.', 'error');
    }
  };

  return (
    <AgentPlatformFeatureGate feature="agents">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
          <AgentPlatformPageHeader
            title="Assistente de IA"
            subtitle="Defina prompts, vínculos e teste conversas no playground integrado."
            area="assistants"
            actions={<Button onClick={() => setShowCreate(true)}>Novo assistente</Button>}
          />

        {showCreate && (
          <form
            onSubmit={handleCreate}
            className={`mb-6 ${apTheme.formPanel} sm:grid-cols-3`}
          >
            <input
              className={apTheme.input}
              placeholder="Código (ex: suporte-ia)"
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              required
            />
            <input
              className={apTheme.input}
              placeholder="Nome"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
            />
            <input
              className={`${apTheme.input} sm:col-span-1`}
              placeholder="Descrição (opcional)"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
            <div className="sm:col-span-3 flex gap-2">
              <Button type="submit">Criar</Button>
              <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        )}

        <div className={apTheme.tableWrap}>
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className={apTheme.tableHead}>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className={apTheme.tableActionsHead}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && agents.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Nenhum assistente cadastrado.
                  </td>
                </tr>
              )}
              {agents.map(agent => (
                <tr key={agent.id} className="hover:bg-red-50/40 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{agent.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 font-mono">{agent.code}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{labelAgentType(agent.type)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={agent.status}
                      label={labelAgentStatus(agent.status)}
                      kind="agent"
                    />
                  </td>
                  <AgentPlatformTableActionsCell>
                    <Link to={`/agent-platform/agents/${agent.id}`} className="inline-flex">
                      <Button variant="secondary" size="sm" type="button">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </Button>
                    </Link>
                    <Button
                      variant="danger"
                      size="sm"
                      type="button"
                      onClick={() => setDeleteTarget(agent)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </AgentPlatformTableActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Excluir assistente"
          message={`Confirma a exclusão de "${deleteTarget?.name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
